/**
 * error-utils — friendly message + copyable debug breadcrumb error system.
 *
 * Wave 1 of the error-messaging overhaul. The problem this solves:
 *
 *   1. **The non-2xx class.** 122 call sites do `await supabase.functions
 *      .invoke(...)` and check `if (error)` but NOT the nested `data.error`
 *      that an edge fn returns in a 200-or-non-2xx body. When an edge fn
 *      fails, the Supabase client surfaces the opaque "Edge Function
 *      returned a non-2xx status code" and the real cause (invalid_grant,
 *      a tool throwing {error}, a Stripe decline) is swallowed.
 *      `unwrapFunctionInvoke` is THE fix — it pulls the nested error out
 *      and throws a typed AppError carrying code + traceId + message.
 *
 *   2. **Opaque user-facing copy.** Errors hit the user as "Failed to X"
 *      with nothing to report. `getFriendlyMessage` maps known codes and
 *      substrings to plain language, and `reportError` always appends a
 *      short `(code: xxx)` breadcrumb the user can read back to support.
 *      That parenthetical IS the copyable breadcrumb in v1 — a real
 *      "Details" expander can come in a later wave.
 *
 *   3. **No structured logging.** `logError` sends to Sentry with
 *      {errorCode, context} tags + {traceId} extra so a support code maps
 *      to exactly one Sentry event.
 *
 * The edge-fn `{error:{code,message,traceId}}` contract is Wave 2 — for
 * now the unwrapper handles BOTH a string `data.error` (current edge fns)
 * AND an object `{code,message,traceId}` (future), so it works against
 * production today and won't need a rewrite when edge fns are upgraded.
 */

import * as Sentry from "@sentry/react";

// =============================================================================
// AppError — the carrier. Throw this from anywhere; getFriendlyMessage,
// logError and reportError all know how to read it.
// =============================================================================

export interface AppErrorInit {
  /** Short machine code from the source (e.g. "invalid_grant", "card_declined"). */
  code?: string;
  /** Server-side trace id, when the edge fn supplies one. */
  traceId?: string;
  /** Friendly, user-facing text. If omitted, getFriendlyMessage derives one. */
  userMessage?: string;
  /** Raw detail for the copyable breadcrumb / Sentry (stack, body, etc.). */
  debugDetail?: string;
  /** Underlying error, preserved for the stack trace. */
  cause?: unknown;
}

/**
 * AppError extends Error so it flows through existing `throw`/`catch` and
 * react-query `onError(error: Error)` paths unchanged. The extra fields are
 * what make it debuggable.
 */
export class AppError extends Error {
  readonly code?: string;
  readonly traceId?: string;
  /** Friendly text safe to show a user. Falls back to `message`. */
  readonly userMessage: string;
  /** Raw, developer-facing detail for the breadcrumb / Sentry extras. */
  readonly debugDetail?: string;

  constructor(message: string, init: AppErrorInit = {}) {
    super(message);
    this.name = "AppError";
    this.code = init.code;
    this.traceId = init.traceId;
    this.userMessage = init.userMessage ?? message;
    this.debugDetail = init.debugDetail;
    if (init.cause !== undefined) {
      // Preserve the underlying error without clobbering our own stack.
      (this as { cause?: unknown }).cause = init.cause;
    }
  }
}

// =============================================================================
// unwrapFunctionInvoke — THE fix for the non-2xx class.
// =============================================================================

/** The shape of `await supabase.functions.invoke(...)`. */
export interface FunctionInvokeResult<T = unknown> {
  data: T | null;
  // FunctionsHttpError / FunctionsRelayError / FunctionsFetchError all extend Error.
  error: (Error & { context?: unknown }) | null;
}

/** Object form an edge fn may return in its body (Wave 2 contract). */
interface EdgeErrorObject {
  code?: string;
  message?: string;
  error?: string;
  traceId?: string;
  trace_id?: string;
}

function isEdgeErrorObject(v: unknown): v is EdgeErrorObject {
  return typeof v === "object" && v !== null;
}

/**
 * Unwrap a `supabase.functions.invoke` result into the success payload, or
 * throw an AppError that carries the real cause.
 *
 * Resolution order:
 *   1. Transport error (FunctionsHttpError etc.) → throw AppError. We try to
 *      read the nested body error first (the actual cause), since the client's
 *      own `.message` is the opaque "non-2xx" string.
 *   2. A nested `data.error` — string OR {code,message,traceId} — even on a
 *      2xx response → throw AppError carrying code + traceId + message.
 *   3. Otherwise return `data as T` (the success path, untouched).
 *
 * @example
 *   const result = await supabase.functions.invoke("ai-agent", { body });
 *   const payload = unwrapFunctionInvoke<{ result: Program }>(result);
 *   // payload is the success body; any edge-fn error has already thrown.
 */
export function unwrapFunctionInvoke<T = unknown>(
  result: FunctionInvokeResult<T>,
): T {
  const { data, error } = result;

  // --- 1. Transport / non-2xx error from the Supabase client.
  if (error) {
    // The real cause is usually in the response body, not error.message
    // (which is the generic "Edge Function returned a non-2xx status code").
    const nested = extractNestedError(data);
    if (nested) {
      throw new AppError(nested.message, {
        code: nested.code,
        traceId: nested.traceId,
        debugDetail: nested.debugDetail ?? error.message,
        cause: error,
      });
    }
    throw new AppError(error.message || "The request failed.", {
      debugDetail: error.message,
      cause: error,
    });
  }

  // --- 2. Nested error in a 2xx body (edge fn returned { error } / { error: {...} }).
  const nested = extractNestedError(data);
  if (nested) {
    throw new AppError(nested.message, {
      code: nested.code,
      traceId: nested.traceId,
      debugDetail: nested.debugDetail,
    });
  }

  // --- 3. Success.
  return data as T;
}

interface NestedError {
  message: string;
  code?: string;
  traceId?: string;
  debugDetail?: string;
}

/**
 * Pull a nested error out of an edge-fn response body, handling both the
 * current string form and the future object form. Returns null when the body
 * carries no error (i.e. it's a success payload). A body with `success: true`
 * is never treated as an error even if it also has an `error` key.
 */
function extractNestedError(data: unknown): NestedError | null {
  if (!isEdgeErrorObject(data)) return null;

  // Explicit success flag wins — never throw on a successful body.
  if ((data as { success?: unknown }).success === true) return null;

  const raw = (data as EdgeErrorObject).error;
  if (raw == null) return null;

  // String form (current edge fns): data.error = "some message".
  if (typeof raw === "string") {
    if (raw.trim() === "") return null;
    return { message: raw, debugDetail: raw };
  }

  // Object form (Wave 2 contract): data.error = { code, message, traceId }.
  if (isEdgeErrorObject(raw)) {
    const obj = raw as EdgeErrorObject;
    const message =
      obj.message || obj.error || obj.code || "The request failed.";
    return {
      message,
      code: obj.code,
      traceId: obj.traceId ?? obj.trace_id,
      debugDetail: safeStringify(raw),
    };
  }

  return null;
}

function safeStringify(v: unknown): string {
  try {
    return typeof v === "string" ? v : JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// =============================================================================
// getFriendlyMessage — map known errors to plain language + a short code.
// =============================================================================

export interface FriendlyMessage {
  /** Short headline for the toast title. */
  title: string;
  /** Plain-language body. */
  userMessage: string;
  /** Short ref the user can read back — source code if present, else generated. */
  code: string;
  /** Server trace id when available. */
  traceId?: string;
}

/**
 * Rule table. First matching rule wins. `match` runs against a lowercased
 * haystack of (code + message + debugDetail), so a rule fires whether the
 * signal arrives as a structured code or buried in an error string.
 */
interface FriendlyRule {
  match: (haystack: string, code?: string) => boolean;
  title: string;
  userMessage: string;
}

const FRIENDLY_RULES: FriendlyRule[] = [
  // --- Auth.
  {
    match: (h) => h.includes("user_already_exists") || h.includes("already registered") || h.includes("already been registered"),
    title: "Email already in use",
    userMessage: "That email is already registered. Try signing in instead.",
  },
  {
    match: (h) => h.includes("invalid login") || h.includes("invalid_credentials") || h.includes("invalid email or password"),
    title: "Sign-in failed",
    userMessage: "Email or password is incorrect. Double-check and try again.",
  },
  {
    match: (h) => h.includes("email not confirmed") || h.includes("email_not_confirmed"),
    title: "Confirm your email",
    userMessage: "Check your inbox and confirm your email before signing in.",
  },
  // --- Calendar / OAuth.
  {
    match: (h) => h.includes("invalid_grant") || h.includes("token expired") || h.includes("token has expired") || h.includes("reconnect"),
    title: "Calendar needs reconnecting",
    userMessage: "Your Google Calendar connection expired. Reconnect it in Settings to keep syncing.",
  },
  // --- Network / timeout.
  {
    match: (h) => h.includes("network") || h.includes("timeout") || h.includes("timed out") || h.includes("failed to fetch") || h.includes("econn") || h.includes("offline"),
    title: "Connection problem",
    userMessage: "Connection problem — check your internet and try again.",
  },
  // --- Stripe / payments.
  {
    match: (h) => h.includes("card_declined") || h.includes("card was declined") || h.includes("card declined"),
    title: "Card declined",
    userMessage: "Your card was declined. Try another card or contact your bank.",
  },
  {
    match: (h) => h.includes("insufficient_funds"),
    title: "Card declined",
    userMessage: "Your card was declined for insufficient funds. Try another card.",
  },
  {
    match: (h) => h.includes("expired_card"),
    title: "Card expired",
    userMessage: "That card has expired. Add a different card and try again.",
  },
  // --- Rate limits / quota.
  {
    match: (h) => h.includes("rate limit") || h.includes("rate_limit") || h.includes("too many requests") || h.includes("quota"),
    title: "Slow down a moment",
    userMessage: "You've hit a limit. Wait a minute and try again.",
  },
  // --- Permissions.
  {
    match: (h) => h.includes("not authenticated") || h.includes("not logged in") || h.includes("unauthorized") || h.includes("jwt"),
    title: "Sign in again",
    userMessage: "Your session expired. Sign in again to continue.",
  },
];

const GENERIC: Omit<FriendlyMessage, "code" | "traceId"> = {
  title: "Something went wrong",
  userMessage: "Something went wrong — try again. If it keeps happening, send us this code.",
};

/**
 * Map any thrown value to friendly text + a short code for the breadcrumb.
 * Always returns a non-empty `code`: the source code if present, else a
 * deterministic-ish 6-char ref so support can find the matching Sentry event.
 */
export function getFriendlyMessage(error: unknown): FriendlyMessage {
  const code = extractCode(error);
  const traceId = extractTraceId(error);
  const haystack = buildHaystack(error);

  for (const rule of FRIENDLY_RULES) {
    if (rule.match(haystack, code)) {
      return {
        title: rule.title,
        userMessage: rule.userMessage,
        code: code ?? generateRef(),
        traceId,
      };
    }
  }

  return {
    title: GENERIC.title,
    userMessage: GENERIC.userMessage,
    code: code ?? generateRef(),
    traceId,
  };
}

function extractCode(error: unknown): string | undefined {
  if (error instanceof AppError && error.code) return error.code;
  if (typeof error === "object" && error !== null) {
    const c = (error as { code?: unknown }).code;
    if (typeof c === "string" && c.trim() !== "") return c;
  }
  return undefined;
}

function extractTraceId(error: unknown): string | undefined {
  if (error instanceof AppError && error.traceId) return error.traceId;
  if (typeof error === "object" && error !== null) {
    const t =
      (error as { traceId?: unknown }).traceId ??
      (error as { trace_id?: unknown }).trace_id;
    if (typeof t === "string" && t.trim() !== "") return t;
  }
  return undefined;
}

function buildHaystack(error: unknown): string {
  const parts: string[] = [];
  if (error instanceof AppError) {
    if (error.code) parts.push(error.code);
    parts.push(error.message);
    if (error.userMessage) parts.push(error.userMessage);
    if (error.debugDetail) parts.push(error.debugDetail);
  } else if (error instanceof Error) {
    parts.push(error.message);
  } else if (typeof error === "string") {
    parts.push(error);
  } else if (typeof error === "object" && error !== null) {
    parts.push(safeStringify(error));
  } else {
    parts.push(String(error));
  }
  return parts.join(" ").toLowerCase();
}

/** Short uppercase alphanumeric ref. Not cryptographic — just human-quotable. */
function generateRef(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no ambiguous chars
  let out = "";
  for (let i = 0; i < 6; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

// =============================================================================
// logError — structured Sentry + console logging.
// =============================================================================

export interface LogResult {
  code: string;
  traceId?: string;
}

/**
 * Log an error to Sentry (tags {errorCode, context}, extra {traceId}) and the
 * console. Returns the code/traceId used so callers can surface the same ref
 * they logged. Never throws — logging failures must not mask the real error.
 */
export function logError(error: unknown, context?: string): LogResult {
  const { code, traceId } = getFriendlyMessage(error);

  try {
    Sentry.captureException(error, {
      tags: { errorCode: code, ...(context ? { context } : {}) },
      ...(traceId ? { extra: { traceId } } : {}),
    });
  } catch {
    // Sentry not initialized / disabled — fine, the console line still lands.
  }

  // Always console.error so it's visible in dev + captured by Sentry breadcrumbs.
  console.error(
    `[error${context ? `:${context}` : ""}] code=${code}${traceId ? ` traceId=${traceId}` : ""}`,
    error,
  );

  return { code, traceId };
}

// =============================================================================
// reportError — the one-liner most callers use.
// =============================================================================

import { toast } from "sonner";

export interface ReportErrorOptions {
  /** Tag for Sentry + logs (e.g. "ai-program-generate", "calendar-sync"). */
  context?: string;
  /** Override the friendly user message (rare — prefer the rule table). */
  fallbackMessage?: string;
}

/**
 * Friendly toast + structured log, in one call. The `(code: xxx)` appended to
 * the toast description IS the copyable breadcrumb in v1. Returns the friendly
 * message (incl. code) so callers can also render it inline if they want.
 *
 * @example
 *   try { ... } catch (err) { reportError(err, { context: "calendar-sync" }); }
 */
export function reportError(
  error: unknown,
  opts: ReportErrorOptions = {},
): FriendlyMessage {
  const friendly = getFriendlyMessage(error);
  const { code, traceId } = logError(error, opts.context);

  // logError re-derives the code; keep the toast and the log in lockstep.
  const finalCode = code || friendly.code;
  const userMessage = opts.fallbackMessage ?? friendly.userMessage;

  toast.error(friendly.title, {
    description: `${userMessage} (code: ${finalCode})`,
  });

  return { ...friendly, code: finalCode, traceId: traceId ?? friendly.traceId };
}
